import { useMemo, useState, type PointerEvent } from 'react';
import {
  MACHINE_SERIES_OVERFLOW_COLOR,
  breachedExtremes,
  machineSeriesColors,
  nearestPointIndex,
  typicalGap,
  type MachineSeries,
  type MachineSeriesPoint,
} from 'tf-pulse-shared';
import { AlarmTriggerMarker } from './AlarmTriggerMarker';
import { ChartTooltip, type ChartTooltipRow } from './ChartTooltip';
import {
  CHART_COLORS,
  CHART_PLOT_HEIGHT,
  X_GRID_FRACTIONS,
  computeYScale,
  describeMachinesWithoutData,
  formatPointerTime,
  formatValue,
  timeToX,
  useContainerWidth,
  xToTime,
} from './chartGeometry';
import type { TimeBounds } from './timeRangeDomain';

const CHART_HEIGHT = CHART_PLOT_HEIGHT;
const TOP_PADDING = 4;
/** A gap this many times the usual sample spacing breaks the line */
const GAP_BREAK_FACTOR = 3;
/** How far from a sample the pointer may be and still read that sample */
const HOVER_TOLERANCE_FACTOR = 1.5;
const NO_DATA_KEY = 'rgb(172, 177, 181)';

interface MachineLineChartProps {
  /** One entry per selected machine, in selection order */
  series: MachineSeries[];
  /** Unit of measurement (e.g., '°', 'bar') */
  unit: string;
  /** The window the X-axis spans */
  timeBounds: TimeBounds;
  /** Labels for the X-axis, spread evenly across the window */
  xAxisLabels: string[];
  /** Alarm thresholds to display as reference lines */
  alarmThresholds?: {
    minimum?: number;
    maximum?: number;
  };
  /** Optional moment (ms) the alarm fired, drawn as a marker */
  alarmTriggerTime?: number;
}

interface HoverRow extends ChartTooltipRow {
  point?: MachineSeriesPoint;
}

/** The vertical stretch of one whisker, in pixels */
interface Whisker {
  /** Where the machine's colour ends and red begins */
  limitY: number;
  /** The extreme the whisker reaches */
  endY: number;
}

/**
 * MachineLineChart - one line per machine for a numeric signal
 *
 * Every reporting machine keeps its own colour (fixed by its order among the
 * reporting machines) so readers can compare machines against each other and
 * against the alarm thresholds. Machines without a sample in the window fold
 * into one legend line. The legend and the hover readout name every machine;
 * colour never stands alone.
 *
 * Each line follows the bucket average. A bucket whose extreme crosses a
 * threshold gets a whisker from the line to that extreme, red past the limit,
 * so a spike reads here as it does in the single-machine bars.
 */
export function MachineLineChart({
  series,
  unit,
  timeBounds,
  xAxisLabels,
  alarmThresholds,
  alarmTriggerTime,
}: MachineLineChartProps) {
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  // Depend on the limit values, not the object, so a parent that rebuilds the
  // thresholds object every render does not redo the work below.
  const minimum = alarmThresholds?.minimum;
  const maximum = alarmThresholds?.maximum;

  // Machines without any sample take no colour slot and no line, so a large
  // alert where most machines are silent still shows the ones that report.
  const { lines, machinesWithoutData } = useMemo(() => {
    const colors = machineSeriesColors(series);
    return {
      lines: series
        .filter((machine) => machine.points.length > 0)
        .map((machine) => {
          const gap = typicalGap(machine.points);
          return {
            ...machine,
            color: colors.get(machine.id) ?? MACHINE_SERIES_OVERFLOW_COLOR,
            gap,
            breakAfter: gap === undefined ? Infinity : gap * GAP_BREAK_FACTOR,
            // Buckets whose extreme crossed a threshold; each gets a whisker
            breaches: machine.points.flatMap((point) => {
              const extremes = breachedExtremes(point, { minimum, maximum });
              return extremes ? [{ point, ...extremes }] : [];
            }),
          };
        }),
      machinesWithoutData: series.filter(
        (machine) => machine.points.length === 0
      ),
    };
  }, [series, minimum, maximum]);

  const hasBreaches = lines.some((line) => line.breaches.length > 0);

  // Include the thresholds so their lines always sit inside the plot
  const { yMin, yMax, ticks } = useMemo(() => {
    // Averages plus every marked extreme, so no whisker leaves the plot
    const values = lines.flatMap((line) => [
      ...line.points.map((point) => point.average),
      ...line.breaches.flatMap((breach) =>
        [breach.above, breach.below].filter(
          (value): value is number => value !== undefined
        )
      ),
    ]);
    const dataMin = Math.min(
      ...values,
      alarmThresholds?.minimum ?? Infinity,
      alarmThresholds?.maximum ?? Infinity
    );
    const dataMax = Math.max(
      ...values,
      alarmThresholds?.minimum ?? -Infinity,
      alarmThresholds?.maximum ?? -Infinity
    );
    return computeYScale(dataMin, dataMax);
  }, [lines, alarmThresholds]);

  const plotHeight = CHART_HEIGHT - TOP_PADDING;
  const toX = (time: number) => timeToX(time, timeBounds, containerWidth);
  const toY = (value: number) =>
    TOP_PADDING + plotHeight * (1 - (value - yMin) / (yMax - yMin));

  const markerTime =
    alarmTriggerTime !== undefined &&
    alarmTriggerTime >= timeBounds.from &&
    alarmTriggerTime <= timeBounds.to
      ? alarmTriggerTime
      : undefined;

  const handlePointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPointer({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

  // The average leads; a marked peak or low follows it, so the tooltip says
  // why a bucket carries a red whisker.
  const describeReading = (point: MachineSeriesPoint) => {
    const reading = `${formatValue(point.average)} ${unit}`.trim();
    const extremes = breachedExtremes(point, { minimum, maximum });
    if (!extremes) return reading;

    const marks: string[] = [];
    if (extremes.above !== undefined) {
      marks.push(`peak ${formatValue(extremes.above)} ${unit}`.trim());
    }
    if (extremes.below !== undefined) {
      marks.push(`low ${formatValue(extremes.below)} ${unit}`.trim());
    }
    return `${reading}, ${marks.join(', ')}`;
  };

  // Snap the crosshair to the nearest sample of any machine and read every
  // machine at that moment, so the pointer never has to land on a line.
  const hover = (() => {
    if (!pointer) return undefined;
    const pointerTime = xToTime(pointer.x, timeBounds, containerWidth);
    let snappedTime = pointerTime;
    let bestDistance = Infinity;

    const rows: HoverRow[] = lines.map((line) => {
      const index = nearestPointIndex(line.points, pointerTime);
      const candidate = index >= 0 ? line.points[index] : undefined;
      const tolerance =
        line.gap === undefined ? Infinity : line.gap * HOVER_TOLERANCE_FACTOR;
      const point =
        candidate && Math.abs(candidate.time - pointerTime) <= tolerance
          ? candidate
          : undefined;

      if (point) {
        const distance = Math.abs(point.time - pointerTime);
        if (distance < bestDistance) {
          bestDistance = distance;
          snappedTime = point.time;
        }
      }

      return {
        key: line.id,
        label: line.label,
        color: line.color,
        value: point ? describeReading(point) : 'no data',
        muted: !point,
        point,
      };
    });

    return { time: snappedTime, rows };
  })();

  const yAxisLabelWidth = useMemo(() => {
    const longestLabelLength = ticks
      .map((tick) => `${tick} ${unit}`.trim())
      .reduce((longest, label) => Math.max(longest, label.length), 0);
    const widthInCh = Math.min(20, Math.max(6, longestLabelLength + 1));
    return `${widthInCh}ch`;
  }, [ticks, unit]);

  return (
    <div className='flex flex-col gap-2.5 select-none'>
      <div className='flex items-start gap-3.5'>
        {/* Y-axis labels */}
        <div
          className='size-caption text-secondary relative ml-1 w-fit min-w-8
            pt-1 pr-1 text-right'
          style={{
            height: CHART_HEIGHT,
            width: yAxisLabelWidth,
            minWidth: yAxisLabelWidth,
          }}
        >
          {ticks
            .slice()
            .reverse()
            .map((tick, index) => {
              const positionPercent = ((yMax - tick) / (yMax - yMin)) * 100;
              return (
                <span
                  key={index}
                  className='absolute right-0 whitespace-nowrap'
                  style={{
                    top: `${positionPercent}%`,
                    transform: 'translateY(-50%)',
                  }}
                >
                  {`${tick} ${unit}`.trim()}
                </span>
              );
            })}
        </div>

        {/* Plot */}
        <div ref={containerRef} className='relative flex-1'>
          <svg
            width='100%'
            height={CHART_HEIGHT}
            className='block overflow-visible'
            onPointerMove={handlePointerMove}
            onPointerLeave={() => setPointer(null)}
          >
            {ticks.map((tick, index) => (
              <line
                key={`grid-${index}`}
                x1={0}
                x2='100%'
                y1={toY(tick)}
                y2={toY(tick)}
                stroke={CHART_COLORS.grid}
                strokeWidth={1}
                strokeDasharray='2 2'
              />
            ))}
            {X_GRID_FRACTIONS.map((fraction) => (
              <line
                key={`vgrid-${fraction}`}
                x1={fraction * containerWidth}
                x2={fraction * containerWidth}
                y1={TOP_PADDING}
                y2={CHART_HEIGHT}
                stroke={CHART_COLORS.grid}
                strokeWidth={1}
                strokeDasharray='2 2'
              />
            ))}

            {alarmThresholds?.minimum !== undefined && (
              <line
                x1={0}
                x2='100%'
                y1={toY(alarmThresholds.minimum)}
                y2={toY(alarmThresholds.minimum)}
                stroke={CHART_COLORS.threshold}
                strokeWidth={2}
                strokeDasharray='5 5'
                opacity={0.75}
              />
            )}
            {alarmThresholds?.maximum !== undefined && (
              <line
                x1={0}
                x2='100%'
                y1={toY(alarmThresholds.maximum)}
                y2={toY(alarmThresholds.maximum)}
                stroke={CHART_COLORS.threshold}
                strokeWidth={2}
                strokeDasharray='5 5'
                opacity={0.75}
              />
            )}

            {/* A whisker from the line to every extreme past a threshold: the
                machine's colour inside the limits, red beyond them */}
            {lines.map((line) =>
              line.breaches.map(({ point, above, below }) => {
                const x = toX(point.time);
                const averageY = toY(point.average);
                const whiskers = [
                  above !== undefined && maximum !== undefined
                    ? {
                        limitY: toY(Math.max(maximum, point.average)),
                        endY: toY(above),
                      }
                    : undefined,
                  below !== undefined && minimum !== undefined
                    ? {
                        limitY: toY(Math.min(minimum, point.average)),
                        endY: toY(below),
                      }
                    : undefined,
                ].filter(
                  (whisker): whisker is Whisker => whisker !== undefined
                );

                return (
                  <g key={`${line.id}-${point.time}`}>
                    {whiskers.map((whisker, index) => (
                      <g key={index}>
                        {whisker.limitY !== averageY && (
                          <line
                            x1={x}
                            x2={x}
                            y1={averageY}
                            y2={whisker.limitY}
                            stroke={line.color}
                            strokeWidth={1}
                            opacity={0.5}
                          />
                        )}
                        <line
                          x1={x}
                          x2={x}
                          y1={whisker.limitY}
                          y2={whisker.endY}
                          stroke={CHART_COLORS.breach}
                          strokeWidth={1.5}
                          strokeLinecap='round'
                        />
                        <circle
                          cx={x}
                          cy={whisker.endY}
                          r={2}
                          fill={CHART_COLORS.breach}
                        />
                      </g>
                    ))}
                  </g>
                );
              })
            )}

            {/* One line per reporting machine; a long gap in samples breaks the line */}
            {lines.map((line) => {
              const path = line.points
                .map((point, index) => {
                  const previous = line.points[index - 1];
                  const startsSegment =
                    !previous || point.time - previous.time > line.breakAfter;
                  const command = startsSegment ? 'M' : 'L';
                  return `${command} ${toX(point.time)} ${toY(point.average)}`;
                })
                .join(' ');
              const last = line.points[line.points.length - 1];

              return (
                <g key={line.id}>
                  <path
                    d={path}
                    fill='none'
                    stroke={line.color}
                    strokeWidth={2}
                    strokeLinejoin='round'
                    strokeLinecap='round'
                  />
                  {last && (
                    <circle
                      cx={toX(last.time)}
                      cy={toY(last.average)}
                      r={4}
                      fill={line.color}
                      stroke={CHART_COLORS.surface}
                      strokeWidth={2}
                    />
                  )}
                </g>
              );
            })}

            {markerTime !== undefined && (
              <AlarmTriggerMarker
                x={toX(markerTime)}
                y1={TOP_PADDING}
                y2={CHART_HEIGHT}
                time={markerTime}
                containerWidth={containerWidth}
              />
            )}

            {hover && (
              <>
                <line
                  x1={toX(hover.time)}
                  x2={toX(hover.time)}
                  y1={TOP_PADDING}
                  y2={CHART_HEIGHT}
                  stroke={CHART_COLORS.crosshair}
                  strokeWidth={1}
                />
                {hover.rows.map(
                  (row) =>
                    row.point && (
                      <circle
                        key={row.key}
                        cx={toX(row.point.time)}
                        cy={toY(row.point.average)}
                        r={4}
                        fill={row.color}
                        stroke={CHART_COLORS.surface}
                        strokeWidth={2}
                      />
                    )
                )}
              </>
            )}
          </svg>

          {pointer && hover && (
            <ChartTooltip
              x={pointer.x}
              y={pointer.y}
              containerWidth={containerWidth}
              title={formatPointerTime(hover.time, timeBounds)}
              rows={hover.rows}
            />
          )}
        </div>
      </div>

      {/* X-axis labels and legend, aligned with the plot */}
      <div className='flex items-start gap-3.5'>
        <div
          className='ml-1 shrink-0'
          style={{ width: yAxisLabelWidth, minWidth: yAxisLabelWidth }}
        />
        <div className='flex flex-1 flex-col gap-2'>
          <div
            className='size-caption text-secondary flex items-center
              justify-between'
          >
            {xAxisLabels.map((label, index) => (
              <span key={index}>{label}</span>
            ))}
          </div>
          <ul className='size-caption text-secondary flex items-center gap-4'>
            {lines.map((line) => (
              <li
                key={line.id}
                className='flex min-w-0 items-center gap-1.5'
                title={line.label}
              >
                <span
                  aria-hidden
                  className='inline-block h-0.5 w-3 shrink-0 rounded-full'
                  style={{ backgroundColor: line.color }}
                />
                <span className='truncate'>{line.label}</span>
              </li>
            ))}
            {machinesWithoutData.length > 0 && (
              <li
                className='flex min-w-0 items-center gap-1.5'
                title={machinesWithoutData
                  .map((machine) => machine.label)
                  .join(', ')}
              >
                <span
                  aria-hidden
                  className='inline-block h-0.5 w-3 shrink-0 rounded-full'
                  style={{ backgroundColor: NO_DATA_KEY }}
                />
                <span className='truncate'>
                  {describeMachinesWithoutData(machinesWithoutData.length)}
                </span>
              </li>
            )}
            {hasBreaches && (
              <li className='flex min-w-0 items-center gap-1.5'>
                <span
                  aria-hidden
                  className='inline-block h-1.5 w-1.5 shrink-0 rounded-full'
                  style={{ backgroundColor: CHART_COLORS.breach }}
                />
                <span className='truncate'>Past the threshold</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
